import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import {
  ErrorMessage,
  LoadingSpinner as LoadingContainer,
  ModalTitle,
} from "@msys/ui";
import { Box, Divider, Stack } from "@mui/material";
import { noop } from "lodash-es";
import React from "react";
import { useParams } from "react-router-dom";
import { RightHandSidebar } from "../../commons/layout/RightHandSidebar.js";
import { PageNotFound } from "../../commons/PageNotFound.js";
import { HeaderBox } from "../doc-items/boxes/HeaderBox.js";
import { RelatedOrderItemsBox } from "../purchase-orders/boxes/RelatedOrderItemsBox.js";
import { BillOfMaterialsItemAlert } from "./BillOfMaterialsItemAlert.js";
import { useBillOfMaterialItemDetailsQuery } from "./BillOfMaterialsItemDetails.generated.js";
import { ItemManualProductBox } from "./boxes/ItemManualProductBox.js";
import { OrderDetailsBox } from "./boxes/OrderDetailsBox.js";
import { ProductBox } from "./boxes/ProductBox.js";
import { RelatedQuoteItemsBox } from "./boxes/RelatedQuoteItemsBox.js";

export function BillOfMaterialsItemDetails({
  projectId,

  handleClose,
}: {
  projectId: string;
  handleClose: () => void;
}) {
  const { itemId } = useParams();
  if (!itemId) throw new Error("BOM item id is missing");

  const relatedQuoteItemsRef = React.useRef<HTMLDivElement | null>(null);

  const client = useApolloClient();
  const query = useBillOfMaterialItemDetailsQuery({
    client,
    variables: {
      projectId,
      itemId,
    },
  });

  const project = getDataOrNull(query.data?.project)?.project;
  const item = project?.billOfMaterialsItems[0];

  return (
    <RightHandSidebar columnSize="lg">
      {query.error ? (
        <ErrorMessage message={query.error.message} />
      ) : query.loading ? (
        <LoadingContainer />
      ) : !item ? (
        <PageNotFound />
      ) : (
        <>
          <Box
            sx={{
              position: "sticky",
              top: 0,
              bgcolor: "background.default",
              zIndex: 1,
            }}
          >
            <ModalTitle handleClose={handleClose} title={item.title} />
            <Divider />
          </Box>
          <Stack
            spacing={1}
            direction={"column"}
            sx={{
              padding: 1,
            }}
          >
            <BillOfMaterialsItemAlert
              item={item}
              handleActionClick={() => {
                relatedQuoteItemsRef.current?.scrollIntoView({
                  behavior: "smooth",
                });
              }}
            />
            {!item.product && (
              <HeaderBox
                projectId={projectId}
                docId={item.relatedQuoteItems[0].quote.id}
                docType="QUOTE"
                item={item.relatedQuoteItems[0].quoteItem}
                viewMode={null}
                setViewMode={noop}
                documentViewerPermissions={project.viewerPermissions}
              />
            )}
            {item.product ? (
              <ProductBox
                productArticleNumber={item.product.articleNumber}
                productSupplierId={item.product.supplierId}
              />
            ) : (
              <ItemManualProductBox
                projectId={projectId}
                docId={item.relatedQuoteItems[0].quote.id}
                itemId={item.relatedQuoteItems[0].quoteItem.id}
                overriddenNetPrice={
                  item.netPrice ?? item.listPrice ?? undefined
                }
              />
            )}
            <OrderDetailsBox billOfMaterialsItem={item} />
            <RelatedOrderItemsBox
              orderItems={item.orderItems}
              project={project}
              document={{ viewerPermissions: project.viewerPermissions }}
            />
            <RelatedQuoteItemsBox
              ref={relatedQuoteItemsRef}
              relatedQuoteItems={item.relatedQuoteItems}
              getItemPath={(docId, itemId) =>
                `/projects/${projectId}/quotes/${docId}/edit/items/${itemId}`
              }
            />
          </Stack>
        </>
      )}
    </RightHandSidebar>
  );
}
